/**
 * Admin メッセージ — shared inbox with employee list + thread (admin mockup).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import { useAdminChatUnread } from '../../../contexts/AdminChatUnreadContext';
import { adminFetch } from '../../../utils/adminFetch';
import {
  chatDateKey,
  chatDayLabelJa,
  chatInboxStampJa,
  chatTimeLabel,
} from '../../../utils/chatMessageTime';
import './AdminMessages.css';

const POLL_MS = 8000;
const NEAR_BOTTOM_PX = 80;

type ConversationItem = {
  conversationId: number;
  empAccountId: number;
  empName: string;
  empEmail: string;
  genba: string | null;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unread: boolean;
};

type EmployeeOption = {
  id: number;
  name: string;
  emailaddress: string;
  genba?: string | null;
};

type ChatMessage = {
  id: number;
  senderType: 'EMPLOYEE' | 'ADMIN';
  senderName: string | null;
  senderAdminEmail: string | null;
  body: string;
  occurredAt: string;
  editedAt?: string | null;
};

type ContextMenuState =
  | {
      kind: 'conversation';
      conversationId: number;
      empName: string;
      x: number;
      y: number;
    }
  | {
      kind: 'message';
      conversationId: number;
      messageId: number;
      body: string;
      x: number;
      y: number;
    }
  | null;

export interface AdminMessagesProps {
  /** Open inbox with this employee selected (e.g. from invitation edit). */
  initialSelectEmail?: string;
  initialInvitationId?: string;
  onInitialSelectConsumed?: () => void;
}

function sortInbox(items: ConversationItem[]): ConversationItem[] {
  return [...items].sort((a, b) => {
    if (a.unread !== b.unread) return a.unread ? -1 : 1;
    const at = a.lastMessageAt ?? '';
    const bt = b.lastMessageAt ?? '';
    if (at !== bt) return bt.localeCompare(at);
    return a.empName.localeCompare(b.empName, 'ja');
  });
}

function matchesSearch(c: ConversationItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [c.empName, c.empEmail, c.genba ?? ''].some((v) => v.toLowerCase().includes(q));
}

const AdminMessages: React.FC<AdminMessagesProps> = ({
  initialSelectEmail,
  initialInvitationId,
  onInitialSelectConsumed,
}) => {
  const { refresh: refreshUnread } = useAdminChatUnread();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [search, setSearch] = useState('');
  const [showEmployeePicker, setShowEmployeePicker] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [draftBeforeEdit, setDraftBeforeEdit] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [threadHeader, setThreadHeader] = useState<{ empName: string; genba: string | null } | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesWrapRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const selectedIdRef = useRef<number | null>(null);
  const lastMessageIdRef = useRef<number | null>(null);
  const initialHandled = useRef(false);

  selectedIdRef.current = selectedId;

  const loadConversations = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoadingList(true);
      setError(null);
    }
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/admin/chat/conversations?page=0&size=100`);
      if (!res.ok) throw new Error('一覧の読み込みに失敗しました');
      const data = await res.json();
      const items: ConversationItem[] = Array.isArray(data.content) ? data.content : [];
      setConversations(sortInbox(items));
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : '読み込みに失敗しました');
        setConversations([]);
      }
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(
    async (conversationId: number, opts?: { silent?: boolean; markRead?: boolean }) => {
      const silent = opts?.silent === true;
      const markRead = opts?.markRead !== false;
      if (!silent) {
        setLoadingThread(true);
        setError(null);
        stickToBottomRef.current = true;
        lastMessageIdRef.current = null;
      }
      try {
        if (markRead) {
          await adminFetch(`${API_BASE_URL}/api/admin/chat/conversations/${conversationId}/read`, {
            method: 'POST',
          });
        }
        const res = await adminFetch(
          `${API_BASE_URL}/api/admin/chat/conversations/${conversationId}/messages?page=0&size=200`
        );
        if (!res.ok) throw new Error('メッセージの読み込みに失敗しました');
        if (selectedIdRef.current !== conversationId) return;
        const data = await res.json();
        const next: ChatMessage[] = Array.isArray(data.messages) ? data.messages : [];
        const nextLastId = next.length > 0 ? next[next.length - 1].id : null;
        const grew = nextLastId != null && nextLastId !== lastMessageIdRef.current;
        lastMessageIdRef.current = nextLastId;

        setThreadHeader({
          empName: data.empName ?? '',
          genba: data.genba ?? null,
        });
        setMessages(next);

        if (silent && grew && !markRead) {
          await adminFetch(`${API_BASE_URL}/api/admin/chat/conversations/${conversationId}/read`, {
            method: 'POST',
          });
          refreshUnread();
          void loadConversations({ silent: true });
        } else if (markRead) {
          refreshUnread();
          void loadConversations({ silent: true });
        }
      } catch (e) {
        if (!silent) {
          setError(e instanceof Error ? e.message : '読み込みに失敗しました');
          setMessages([]);
        }
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [loadConversations, refreshUnread]
  );

  const selectConversation = useCallback(
    (conversationId: number) => {
      selectedIdRef.current = conversationId;
      setEditingMessageId(null);
      setDraftBeforeEdit('');
      setSelectedId(conversationId);
      setDraft('');
      setMessages([]);
      void loadThread(conversationId, { markRead: true, silent: false });
    },
    [loadThread]
  );

  const ensureAndSelect = useCallback(
    async (email: string, invitationId?: string) => {
      const params = new URLSearchParams({ email });
      if (invitationId?.trim()) params.set('invitationId', invitationId.trim());
      const res = await adminFetch(
        `${API_BASE_URL}/api/admin/chat/conversations/ensure?${params}`
      );
      if (!res.ok) throw new Error('会話の開始に失敗しました');
      const item: ConversationItem = await res.json();
      await loadConversations();
      selectConversation(item.conversationId);
    },
    [loadConversations, selectConversation]
  );

  const loadEmployeeOptions = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/employees`);
      if (!res.ok) throw new Error('従業員一覧の読み込みに失敗しました');
      const data = await res.json();
      const items: EmployeeOption[] = Array.isArray(data) ? data : [];
      items.sort((a, b) => {
        const an = a.name ?? '';
        const bn = b.name ?? '';
        return an.localeCompare(bn, 'ja');
      });
      setEmployeeOptions(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました');
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  const handleOpenEmployeePicker = useCallback(() => {
    setShowEmployeePicker(true);
    setEmployeeSearch('');
    if (employeeOptions.length === 0 && !loadingEmployees) {
      void loadEmployeeOptions();
    }
  }, [employeeOptions.length, loadingEmployees, loadEmployeeOptions]);

  const handleStartConversation = useCallback(
    async (email: string) => {
      await ensureAndSelect(email);
      setShowEmployeePicker(false);
      setEmployeeSearch('');
    },
    [ensureAndSelect]
  );

  const handleArchiveConversation = useCallback(
    async (conversationId: number, empName: string) => {
      const ok = window.confirm(`「${empName || 'この従業員'}」との会話をアーカイブしますか？`);
      if (!ok) return;
      setContextMenu(null);
      try {
        const res = await adminFetch(
          `${API_BASE_URL}/api/admin/chat/conversations/${conversationId}/archive`,
          { method: 'POST' }
        );
        if (!res.ok) throw new Error('会話のアーカイブに失敗しました');
        if (selectedIdRef.current === conversationId) {
          selectedIdRef.current = null;
          setEditingMessageId(null);
          setDraftBeforeEdit('');
          setSelectedId(null);
          setThreadHeader(null);
          setMessages([]);
          setDraft('');
        }
        refreshUnread();
        await loadConversations();
      } catch (e) {
        setError(e instanceof Error ? e.message : '会話のアーカイブに失敗しました');
      }
    },
    [loadConversations, refreshUnread]
  );

  const handleEditMessage = useCallback(
    async (_conversationId: number, messageId: number, currentBody: string) => {
      setContextMenu(null);
      setDraftBeforeEdit(draft);
      setEditingMessageId(messageId);
      setDraft(currentBody);
    },
    [draft]
  );

  const handleDeleteMessage = useCallback(
    async (conversationId: number, messageId: number) => {
      const ok = window.confirm('このメッセージを削除しますか？');
      if (!ok) return;
      setContextMenu(null);
      try {
        const res = await adminFetch(
          `${API_BASE_URL}/api/admin/chat/conversations/${conversationId}/messages/${messageId}`,
          { method: 'DELETE' }
        );
        if (!res.ok) throw new Error('メッセージの削除に失敗しました');
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        void loadConversations({ silent: true });
        refreshUnread();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'メッセージの削除に失敗しました');
      }
    },
    [loadConversations, refreshUnread]
  );

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!initialSelectEmail?.trim() || initialHandled.current) return;
    initialHandled.current = true;
    void ensureAndSelect(initialSelectEmail, initialInvitationId).finally(() => {
      onInitialSelectConsumed?.();
    });
  }, [initialSelectEmail, initialInvitationId, ensureAndSelect, onInitialSelectConsumed]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void loadConversations({ silent: true });
      const current = selectedIdRef.current;
      if (current != null) {
        void loadThread(current, { silent: true, markRead: false });
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [loadConversations, loadThread]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (contextMenuRef.current && target && contextMenuRef.current.contains(target)) {
        return;
      }
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: loadingThread ? 'auto' : 'smooth' });
  }, [messages, loadingThread]);

  const handleMessagesScroll = () => {
    const el = messagesWrapRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!selectedId || !text || sending) return;
    setSending(true);
    setError(null);
    try {
      if (editingMessageId != null) {
        const res = await adminFetch(
          `${API_BASE_URL}/api/admin/chat/conversations/${selectedId}/messages/${editingMessageId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ body: text }),
          }
        );
        if (!res.ok) throw new Error('メッセージの編集に失敗しました');
        const updated: ChatMessage = await res.json();
        setMessages((prev) => prev.map((m) => (m.id === editingMessageId ? updated : m)));
        setEditingMessageId(null);
        setDraftBeforeEdit('');
        setDraft('');
        void loadConversations({ silent: true });
      } else {
        const res = await adminFetch(
          `${API_BASE_URL}/api/admin/chat/conversations/${selectedId}/messages`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ body: text }),
          }
        );
        if (!res.ok) throw new Error('送信に失敗しました');
        const msg: ChatMessage = await res.json();
        stickToBottomRef.current = true;
        lastMessageIdRef.current = msg.id;
        setMessages((prev) => [...prev, msg]);
        setDraft('');
        refreshUnread();
        void loadConversations({ silent: true });
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : editingMessageId != null
            ? 'メッセージの編集に失敗しました'
            : '送信に失敗しました'
      );
    } finally {
      setSending(false);
    }
  };

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setDraft(draftBeforeEdit);
    setDraftBeforeEdit('');
  }, [draftBeforeEdit]);

  const visibleConversations = useMemo(
    () => conversations.filter((c) => matchesSearch(c, search)),
    [conversations, search]
  );

  const visibleEmployeeOptions = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return employeeOptions;
    return employeeOptions.filter((e) =>
      [e.name ?? '', e.emailaddress ?? ''].some((v) => v.toLowerCase().includes(q))
    );
  }, [employeeOptions, employeeSearch]);

  return (
    <div className="admin-messages">
      <h1 className="admin-messages__title">メッセージ</h1>
      {error && (
        <p className="admin-messages__error" role="alert">
          {error}
        </p>
      )}
      <div className="admin-messages__layout">
        <aside className="admin-messages__sidebar" aria-label="従業員一覧">
          <div className="admin-messages__search-wrap">
            <input
              type="search"
              className="admin-messages__search"
              placeholder="氏名・メール・現場で検索"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="会話を検索"
            />
          </div>
          {loadingList ? (
            <p className="admin-messages__loading">読み込み中…</p>
          ) : conversations.length === 0 ? (
            <p className="admin-messages__empty">会話はまだありません。</p>
          ) : visibleConversations.length === 0 ? (
            <p className="admin-messages__empty">該当する会話がありません。</p>
          ) : (
            <ul className="admin-messages__contact-list">
              {visibleConversations.map((c) => (
                <li key={c.conversationId}>
                  <button
                    type="button"
                    className={`admin-messages__contact${
                      selectedId === c.conversationId ? ' admin-messages__contact--active' : ''
                    }${c.unread ? ' admin-messages__contact--unread' : ''}`}
                    onClick={() => selectConversation(c.conversationId)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        kind: 'conversation',
                        conversationId: c.conversationId,
                        empName: c.empName,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }}
                  >
                    <span className="admin-messages__contact-main">
                      <span className="admin-messages__contact-top">
                        <span className="admin-messages__contact-name">{c.empName}</span>
                        <span className="admin-messages__contact-stamp">
                          {chatInboxStampJa(c.lastMessageAt)}
                        </span>
                      </span>
                      {c.genba && (
                        <span className="admin-messages__contact-genba">{c.genba}</span>
                      )}
                      <span className="admin-messages__contact-preview">
                        {c.lastMessagePreview || '（メッセージなし）'}
                      </span>
                    </span>
                    {c.unread && <span className="admin-messages__unread-dot" aria-label="未読" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="admin-messages__thread" aria-label="チャット">
          {!selectedId ? (
            <div className="admin-messages__placeholder admin-messages__placeholder--empty">
              <p className="admin-messages__placeholder-copy">左から従業員を選択してください。</p>
              <p className="admin-messages__placeholder-subcopy">
                氏名またはメールアドレスから従業員を選んでメッセージを開始できます。
              </p>
              {!showEmployeePicker ? (
                <button
                  type="button"
                  className="admin-messages__new-thread-btn"
                  onClick={handleOpenEmployeePicker}
                >
                  ＋ 新しくメッセージを送る
                </button>
              ) : (
                <div className="admin-messages__employee-picker">
                  <div className="admin-messages__employee-picker-top">
                    <input
                      type="search"
                      className="admin-messages__employee-search"
                      placeholder="氏名またはメールアドレスで検索"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      aria-label="従業員を検索"
                    />
                    <button
                      type="button"
                      className="admin-messages__employee-cancel"
                      onClick={() => setShowEmployeePicker(false)}
                    >
                      キャンセル
                    </button>
                  </div>
                  <div className="admin-messages__employee-list" aria-label="従業員一覧">
                    {loadingEmployees ? (
                      <p className="admin-messages__empty">従業員一覧を読み込み中…</p>
                    ) : visibleEmployeeOptions.length === 0 ? (
                      <p className="admin-messages__empty">該当する従業員がありません。</p>
                    ) : (
                      <ul className="admin-messages__employee-items">
                        {visibleEmployeeOptions.map((emp) => (
                          <li key={emp.id}>
                            <button
                              type="button"
                              className="admin-messages__employee-item"
                              onClick={() => void handleStartConversation(emp.emailaddress)}
                            >
                              <span className="admin-messages__employee-name">{emp.name || '—'}</span>
                              <span className="admin-messages__employee-email">{emp.emailaddress}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <header className="admin-messages__thread-header">
                <span className="admin-messages__thread-name">{threadHeader?.empName ?? '…'}</span>
                {threadHeader?.genba && (
                  <span className="admin-messages__thread-genba">{threadHeader.genba}</span>
                )}
              </header>

              <div
                className="admin-messages__messages"
                ref={messagesWrapRef}
                onScroll={handleMessagesScroll}
              >
                {loadingThread ? (
                  <p className="admin-messages__loading">読み込み中…</p>
                ) : messages.length === 0 ? (
                  <p className="admin-messages__empty-thread">メッセージはまだありません。</p>
                ) : (
                  messages.map((m, i) => {
                    const isAdmin = m.senderType === 'ADMIN';
                    const prevKey = i > 0 ? chatDateKey(messages[i - 1].occurredAt) : '';
                    const showDay = chatDateKey(m.occurredAt) !== prevKey;
                    const time = chatTimeLabel(m.occurredAt);
                    return (
                      <React.Fragment key={m.id}>
                        {showDay && (
                          <div className="admin-messages__day" role="separator">
                            <span>{chatDayLabelJa(m.occurredAt)}</span>
                          </div>
                        )}
                        <div
                          className={`admin-messages__bubble-row${
                            isAdmin ? ' admin-messages__bubble-row--admin' : ''
                          }`}
                        >
                          <div className="admin-messages__bubble-cluster">
                            {isAdmin && time && (
                              <span className="admin-messages__time">{time}</span>
                            )}
                            <div
                              className={`admin-messages__bubble${
                                isAdmin
                                  ? ' admin-messages__bubble--admin'
                                  : ' admin-messages__bubble--employee'
                              }`}
                              onContextMenu={
                                isAdmin
                                  ? (e) => {
                                      e.preventDefault();
                                      setContextMenu({
                                        kind: 'message',
                                        conversationId: selectedId,
                                        messageId: m.id,
                                        body: m.body,
                                        x: e.clientX,
                                        y: e.clientY,
                                      });
                                    }
                                  : undefined
                              }
                            >
                              {m.body}
                              {m.editedAt && (
                                <span className="admin-messages__edited-label">編集済み</span>
                              )}
                            </div>
                            {!isAdmin && time && (
                              <span className="admin-messages__time">{time}</span>
                            )}
                          </div>
                          {isAdmin && m.senderName && (
                            <span className="admin-messages__sender-label">{m.senderName}</span>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <footer className="admin-messages__composer">
                {editingMessageId != null && (
                  <div className="admin-messages__edit-banner">
                    <span>メッセージを編集中</span>
                    <button
                      type="button"
                      className="admin-messages__edit-cancel"
                      onClick={handleCancelEdit}
                    >
                      キャンセル
                    </button>
                  </div>
                )}
                <textarea
                  className="admin-messages__input"
                  rows={2}
                  value={draft}
                  placeholder={editingMessageId != null ? 'メッセージを編集…' : 'メッセージを入力…'}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                  disabled={sending}
                />
                <button
                  type="button"
                  className="admin-messages__send-btn"
                  onClick={() => void handleSend()}
                  disabled={sending || !draft.trim()}
                >
                  {editingMessageId != null ? '更新' : '送信'}
                </button>
              </footer>
            </>
          )}
        </section>
      </div>
      {contextMenu && (
        <div
          className="admin-messages__context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          ref={contextMenuRef}
          role="menu"
          aria-label={contextMenu.kind === 'conversation' ? '会話メニュー' : 'メッセージメニュー'}
          onContextMenu={(e) => e.preventDefault()}
        >
          {contextMenu.kind === 'conversation' ? (
            <button
              type="button"
              className="admin-messages__context-item"
              onClick={() =>
                void handleArchiveConversation(contextMenu.conversationId, contextMenu.empName)
              }
            >
              アーカイブ
            </button>
          ) : (
            <>
              <button
                type="button"
                className="admin-messages__context-item"
                onClick={() =>
                  void handleEditMessage(
                    contextMenu.conversationId,
                    contextMenu.messageId,
                    contextMenu.body
                  )
                }
              >
                編集
              </button>
              <button
                type="button"
                className="admin-messages__context-item"
                onClick={() =>
                  void handleDeleteMessage(contextMenu.conversationId, contextMenu.messageId)
                }
              >
                削除
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
